import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Users,
  Calendar,
  User,
  LogOut,
  Menu,
  X,
  Shield,
  Mail,
} from "lucide-react";
import { useAuthStore } from "../store/authStore";
import { useMessageStore } from "../store/messageStore";
import { useAuth } from "../contexts/AuthContext";
import { ProfileAvatar, FloatingNavigation, NotificationBell, CopyrightFooter } from "../components/ui";

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { hasUnreadMessages, checkUnreadMessages } = useMessageStore();
  const { logout } = useAuth();

  // Check for unread messages when layout mounts and user is available
  useEffect(() => {
    if (user) {
      checkUnreadMessages();
    }
  }, [user]);

  const handleMailIconClick = () => {
    // When navigating to messages page, update unread status
    if (location.pathname !== '/messages') {
      // We're navigating TO the messages page, so update unread status
      setTimeout(() => checkUnreadMessages(), 100);
    }
  };

  const navigation = [
    {
      name: "Dashboard",
      href: "/dashboard",
      icon: Users,
      current: location.pathname === "/dashboard",
    },
    {
      name: "Evenementen",
      href: "/events",
      icon: Calendar,
      current: location.pathname.startsWith("/events"),
    },
    {
      name: "Groepen",
      href: "/groups",
      icon: Users,
      current: location.pathname.startsWith("/groups"),
    },
    {
      name: "Profiel",
      href: "/profile",
      icon: User,
      current: location.pathname === "/profile",
    },
    ...(user?.isAdmin ? [{
      name: "Admin",
      href: "/admin",
      icon: Shield,
      current: location.pathname === "/admin",
    }] : []),
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
      // Force logout even if API call fails
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen">
      {/* Floating Navigation (desktop) */}
      <FloatingNavigation />

      {/* Mobile Navigation */}
      <nav className="lg:hidden sticky top-0 z-50 border-b border-white/[.09] bg-[rgba(17,17,25,.85)] backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between h-16">
            {/* Logo */}
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2.5"
                >
                  <div className="sf-logo-mark h-8 w-8 text-lg">S</div>
                  <span className="text-lg font-extrabold tracking-tight text-fg-bright">
                    SOLOFIX
                  </span>
                </Link>
              </div>
            </div>

            {/* Mobile menu button and icons */}
            <div className="lg:hidden flex items-center space-x-1">
              {/* Notification Bell */}
              <NotificationBell />

              {/* Messages Icon */}
              <Link
                to="/messages"
                className="relative p-2 text-fg-nav hover:text-fg-bright rounded-xl transition-colors"
                onClick={handleMailIconClick}
              >
                <Mail className="h-5 w-5" />
                {hasUnreadMessages && (
                  <span className="absolute top-0.5 right-0.5 h-2 w-2 bg-neon-pink rounded-full shadow-neon-pink-strong" />
                )}
              </Link>

              {/* Mobile menu toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 text-fg-nav hover:text-fg-bright focus:outline-none"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-white/[.06]">
            <div className="pt-2 pb-3 space-y-1 px-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`${
                      item.current
                        ? "bg-neon-gradient-soft border border-neon-pink/[.32] text-fg-bright"
                        : "text-fg-nav hover:bg-white/[.05] hover:text-fg-bright"
                    } block px-3 py-2.5 rounded-xl text-base font-medium transition-colors duration-200`}
                  >
                    <div className="flex items-center">
                      <Icon className="h-5 w-5 mr-3" />
                      {item.name}
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="pt-4 pb-3 border-t border-white/[.08]">
              <Link
                to="/profile"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center px-4 py-2 hover:bg-white/[.05] transition-colors duration-200 rounded-xl mx-2"
              >
                <ProfileAvatar
                  user={user}
                  size="md"
                />
                <div className="ml-3">
                  <div className="text-base font-semibold text-fg-bright">
                    {user?.username}
                  </div>
                  <div className="text-sm text-fg-muted">
                    {user?.email}
                  </div>
                </div>
              </Link>
              <div className="mt-3 space-y-1">
                <button
                  onClick={handleLogout}
                  className="block px-4 py-2 text-base font-medium text-fg-muted hover:text-neon-pink-soft hover:bg-white/[.05] w-full text-left transition-colors duration-200"
                >
                  <div className="flex items-center">
                    <LogOut className="h-5 w-5 mr-3" />
                    Uitloggen
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main content */}
      <main className="flex-1 lg:pt-24">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          {children}
          <CopyrightFooter />
        </div>
      </main>
    </div>
  );
};

export default MainLayout;
